<template>
  <div class="vtmn-h-screen vtmn-flex vtmn-flex-col vtmn-overflow-hidden">
    <header
      class="vtmn-h-16 vtmn-bg-white vtmn-border-b vtmn-border-gray-200 vtmn-flex vtmn-items-center vtmn-px-4 vtmn-shrink-0 vtmn-justify-between vtmn-z-50 vtmn-relative"
    >
      <div class="vtmn-flex vtmn-items-center">
        <button
          @click.stop="handleToggle"
          data-testid="btn-toggle"
          class="vtmn-mr-4 vtmn-p-2 vtmn-rounded hover:vtmn-bg-gray-100 vtmn-text-gray-600 vtmn-flex vtmn-items-center"
        >
          <span :class="menuIconClass" class="vtmn-text-2xl"></span>
        </button>
        <div class="vtmn-flex vtmn-items-center vtmn-gap-3">
          <img src="@/assets/logo.svg" alt="logo decathlon" class="vtmn-h-7" />
          <div class="vtmn-h-6 vtmn-w-px vtmn-bg-gray-300 mx-2"></div>
          <span class="vtmn-font-bold vtmn-text-lg vtmn-text-blue-800"> {{ currentModuleTitle }}</span>
        </div>
      </div>
    </header>

    <div class="vtmn-flex vtmn-flex-1 vtmn-overflow-hidden vtmn-relative">
      <div
        v-if="isMobileOpen"
        @click="closeMobileSidebar"
        class="vtmn-fixed vtmn-inset-0 vtmn-bg-opacity-50 vtmn-z-20 md:vtmn-hidden transition-opacity"
        data-testid="mobile-overlay"
      ></div>

      <aside
        @click.stop
        class="vtmn-bg-white vtmn-border-r vtmn-border-gray-200 vtmn-flex vtmn-flex-col vtmn-transition-all vtmn-duration-300 vtmn-ease-in-out vtmn-z-30 vtmn-overflow-hidden"
        data-testid="sidebar"
        :class="sidebarClasses"
      >
        <div
          class="vtmn-flex vtmn-items-center vtmn-h-16 vtmn-transition-all vtmn-duration-300 vtmn-border-b vtmn-border-gray-200 vtmn-p-0 vtmn-overflow-hidden"
        >
          <div class="icon-container flex-shrink-0">
            <div
              class="vtmn-h-8 vtmn-w-8 vtmn-rounded-full vtmn-bg-blue-700 vtmn-text-white vtmn-flex vtmn-items-center vtmn-justify-center vtmn-font-bold vtmn-text-sm"
              data-testid="user-initials"
            >
              {{ userInitials }}
            </div>
          </div>

          <div
            class="vtmn-flex vtmn-flex-col vtmn-justify-center vtmn-transition-opacity vtmn-duration-300 vtmn-whitespace-nowrap"
            :class="{ 'vtmn-opacity-100': isDesktopExpanded || isMobileOpen, 'vtmn-opacity-0': !isDesktopExpanded }"
          >
            <p class="vtmn-font-bold vtmn-text-sm vtmn-text-gray-900">{{ user.name }}</p>
            <p class="vtmn-text-xs vtmn-text-gray-500">{{ user.job }}</p>
          </div>
        </div>

        <nav class="vtmn-flex-1 vtmn-py-4 vtmn-space-y-1 vtmn-overflow-x-hidden vtmn-capitalize">
          <router-link
            v-for="item in visibleMenuItems"
            :key="item.routeName"
            :to="{ name: item.routeName }"
            class="nav-item group"
            :class="{ 'nav-item-active': isItemActive(item) }"
            :title="!isDesktopExpanded && !isMobileOpen ? ($te(item.label) ? $t(item.label) : item.label) : ''"
            @click="handleLinkClick"
          >
            <div class="icon-container">
              <span :class="[item.icon, 'vtmn-text-2xl group-hover:vtmn-text-blue-700']"></span>
            </div>
            <span
              class="nav-text"
              :class="{
                'vtmn-opacity-100': isDesktopExpanded || isMobileOpen,
                'vtmn-opacity-0': !isDesktopExpanded && !isMobileOpen,
              }"
            >
              {{ $te(item.label) ? $t(item.label) : item.label }}
            </span>
          </router-link>
        </nav>

        <a
          v-if="!isKioskModeActive"
          href="https://support.decathlon.net/saw/ess/offeringPage/107014470"
          target="_blank"
          data-testid="support-link"
          class="nav-item group vtmn-w-full hover:vtmn-bg-gray-100"
          :title="!isDesktopExpanded && !isMobileOpen ? $t('ask_support') : ''"
        >
          <div class="icon-container">
            <span class="vtmx-customer-service-line vtmn-text-2xl vtmn-text-gray-600 group-hover:vtmn-text-blue-700"></span>
          </div>
          <span
            class="nav-text vtmn-text-gray-600"
            :class="{ 'vtmn-opacity-100': isDesktopExpanded || isMobileOpen, 'vtmn-opacity-0': !isDesktopExpanded }"
          >
            {{ $t("ask_support") }}
          </span>
        </a>

        <div class="vtmn-border-t vtmn-border-gray-200 vtmn-p-0 vtmn-overflow-hidden">
          <button
            @click="onLogout"
            class="nav-item group vtmn-w-full hover:vtmn-bg-red-50"
            data-testid="btn-logout"
            :title="!isDesktopExpanded && !isMobileOpen ? $t('logout') : ''"
          >
            <div class="icon-container">
              <span class="vtmx-logout-line vtmn-text-2xl vtmn-text-red-600 group-hover:vtmn-text-red-700"></span>
            </div>
            <span
              class="nav-text vtmn-text-red-600"
              :class="{ 'vtmn-opacity-100': isDesktopExpanded || isMobileOpen, 'vtmn-opacity-0': !isDesktopExpanded }"
            >
              {{ $t("logout") }}
            </span>
          </button>
        </div>
      </aside>

      <main
        class="vtmn-flex-1 vtmn-relative vtmn-flex vtmn-flex-col vtmn-min-w-0"
        data-testid="main-content"
        @click="closeDesktopSidebar"
      >
        <button
          v-if="!isMobileScreen && isKioskRouteActive"
          @click="onActivateKioskMode"
          class="vtmn-absolute vtmn-top-6 vtmn-left-0 vtmn-z-40 vtmn-h-10 vtmn-w-6 vtmn-overflow-hidden vtmn-border vtmn-border-l-0 vtmn-rounded-r vtmn-shadow-md vtmn-flex vtmn-items-center vtmn-justify-center vtmn-transition-colors vtmn-duration-300"
          :class="
            isKioskModeActive
              ? 'vtmn-text-blue-700'
              : 'vtmn-bg-white vtmn-border-gray-200 vtmn-text-gray-500 hover:vtmn-text-blue-700'
          "
          data-testid="btn-toggle-visibility"
          :title="isKioskModeActive ? 'Modo quiosco activo: pulse F9 para mostrar menú' : 'Activar modo quiosco'"
        >
          <span class="vtmx-lock-line vtmn-text-xl"></span>
        </button>

        <div class="vtmn-flex-1 vtmn-overflow-y-auto vtmn-bg-gray-50">
          <slot></slot>
        </div>
      </main>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import useAuth from "@/modules/oauth/composables/useAuth";
import fedidService from "../../modules/oauth/services/FedidService";
import { useRoute } from "vue-router";
import { ROLE_APPS, ROLE_NAMES } from "@/modules/oauth/constants/roles";
import useKioskMode from "@/composables/useKioskMode";

const route = useRoute();
const {
  isKioskModeActive,
  isKioskRoute,
  activateKioskMode,
  deactivateKioskMode,
  syncKioskModeFromStorage,
  KIOSK_ALLOWED_ROUTE_NAMES,
} = useKioskMode();

const isDesktopExpanded = ref(false);
const isMobileOpen = ref(false);
const isMobileScreen = ref(false);
const isSidebarHidden = ref(false);
const isKioskRouteActive = computed(() => isKioskRoute(route.path));

const currentModuleTitle = computed(() => {
  const parts = route.path.split("/").filter(Boolean);
  const moduleName = parts.length > 0 ? parts[0] : "hda";

  const titles: Record<string, string> = {
    hda: "HDA",
    planning: "PLANNING",
    configuration: "CONFIGURATION",
    visitor: "VISITOR",
    bookings: "BOOKINGS",
  };
  return titles[moduleName] || moduleName.toUpperCase();
});

const checkScreenSize = () => {
  isMobileScreen.value = window.innerWidth < 768;
  if (!isMobileScreen.value) {
    isMobileOpen.value = false;
  }
};

onMounted(() => {
  checkScreenSize();
  window.addEventListener("resize", checkScreenSize);
  globalThis.addEventListener("keydown", handleKeyDown);
  syncKioskModeFromStorage();
  isSidebarHidden.value = false;
});
onUnmounted(() => {
  window.removeEventListener("resize", checkScreenSize);
  globalThis.removeEventListener("keydown", handleKeyDown);
});

const menuIconClass = computed(() => {
  if (isMobileScreen.value && isMobileOpen.value) {
    return "vtmx-close-line";
  }
  return "vtmx-menu-line";
});

const sidebarClasses = computed(() => {
  if (isMobileScreen.value) {
    return [
      "vtmn-fixed",
      "vtmn-top-16",
      "vtmn-left-0",
      "vtmn-bottom-0",
      "vtmn-w-full",
      isMobileOpen.value ? "vtmn-translate-x-0" : "-vtmn-translate-x-full",
    ];
  } else {
    if (isSidebarHidden.value) {
      return ["vtmn-w-0", "vtmn-border-none"];
    }
    return ["vtmn-relative", "vtmn-translate-x-0", isDesktopExpanded.value ? "vtmn-w-72" : "vtmn-w-16"];
  }
});

const handleToggle = () => {
  if (isMobileScreen.value) {
    isMobileOpen.value = !isMobileOpen.value;
  } else if (isSidebarHidden.value) {
    isSidebarHidden.value = false;
  } else {
    isDesktopExpanded.value = !isDesktopExpanded.value;
  }
};

const onActivateKioskMode = () => {
  if (isKioskModeActive.value) {
    return;
  }
  activateKioskMode();
  isSidebarHidden.value = false;
};

const handleKeyDown = (event: KeyboardEvent) => {
  if (event.key === "F9") {
    event.preventDefault();
    if (isKioskModeActive.value) {
      deactivateKioskMode();
      isSidebarHidden.value = false;
    }
  }
};

const handleLinkClick = () => {
  if (isMobileScreen.value) {
    isMobileOpen.value = false;
  } else {
    isDesktopExpanded.value = false;
  }
};

const closeMobileSidebar = () => {
  isMobileOpen.value = false;
};

const closeDesktopSidebar = () => {
  if (!isMobileScreen.value && isDesktopExpanded.value) {
    isDesktopExpanded.value = false;
  }
};

const user = ref({ name: "", email: "", job: "" });
const hdaRoles = ref<string[]>([]);
const dassistRoles = ref<string[]>([]);
const { userInfo, getRolesByAppName } = useAuth();

const onLogout = () => fedidService.logout();

const userInitials = computed(() => {
  if (!user.value.name) return "U";
  const names = user.value.name.split(" ");
  let initials = names[0].substring(0, 1).toUpperCase();
  if (names.length > 1) initials += names[names.length - 1].substring(0, 1).toUpperCase();
  return initials;
});

const updateUserData = () => {
  if (userInfo.value) {
    user.value.name = userInfo.value.displayName || "";
    user.value.job = userInfo.value?.jobname || "";
    hdaRoles.value = getRolesByAppName(ROLE_APPS.HDA_ROLES)?.map((role) => role.role) || [];
    dassistRoles.value = getRolesByAppName(ROLE_APPS.DASSIST_ROLES)?.map((role) => role.role) || [];
  }
};
watch(userInfo, updateUserData);
updateUserData();

const menuConfig = [
  {
    label: "hda",
    routeName: "collaborator",
    icon: "vtmx-home-line",
    allowedRoles: [
      ROLE_NAMES.HDA_IT_SUPERADMIN,
      ROLE_NAMES.HDA_SUPERADMIN,
      ROLE_NAMES.HDA_OPERATOR,
      ROLE_NAMES.WAREHOUSEMANAGER,
      ROLE_NAMES.ECONOMIC,
    ],
    prefix: "/hda",
    appContext: ROLE_APPS.HDA_ROLES,
  },
  {
    label: "visitors",
    routeName: "visitors",
    icon: "vtmx-group-line",
    allowedRoles: [ROLE_NAMES.VISITORS, ROLE_NAMES.VISITORS_ADMIN, ROLE_NAMES.DASSIST_SUPERADMIN],
    prefix: "/visitors",
    appContext: ROLE_APPS.DASSIST_ROLES,
  },
  {
    label: "delays",
    routeName: "delays",
    icon: "vtmx-time-line",
    allowedRoles: [ROLE_NAMES.VISITORS, ROLE_NAMES.DELAYS_ADMIN, ROLE_NAMES.DASSIST_SUPERADMIN],
    prefix: "/delays",
    appContext: ROLE_APPS.DASSIST_ROLES,
  },
  {
    label: "bookings",
    routeName: "bookingsHome",
    icon: "vtmx-calendar-check-line",
    allowedRoles: [ROLE_NAMES.DASSIST_SUPERADMIN],
    prefix: "/bookings",
    appContext: ROLE_APPS.DASSIST_ROLES,
  },
  {
    label: "configuration",
    routeName: "configuration",
    icon: "vtmx-settings-line",
    allowedRoles: [ROLE_NAMES.HDA_SUPERADMIN, ROLE_NAMES.VISITORS_ADMIN, ROLE_NAMES.DASSIST_SUPERADMIN],
    prefix: "/configuration",
    appContext: "BOTH",
  },
];

type MenuItem = { label: string; routeName: string; icon: string; allowedRoles: string[]; prefix: string };

const isItemActive = (item: MenuItem) => {
  return route.path.startsWith(item.prefix);
};

const visibleMenuItems = computed(() => {
  if (!hdaRoles.value && !dassistRoles.value) return [];

  const roleFiltered = menuConfig.filter((item) => {
    if (item.allowedRoles.length === 0) return true;

    let rolesToCheck: string[] = [];
    if (item.appContext === ROLE_APPS.HDA_ROLES) {
      rolesToCheck = hdaRoles.value || [];
    } else if (item.appContext === ROLE_APPS.DASSIST_ROLES) {
      rolesToCheck = dassistRoles.value || [];
    } else if (item.appContext === "BOTH") {
      rolesToCheck = [...(hdaRoles.value || []), ...(dassistRoles.value || [])];
    }

    return item.allowedRoles.some((requiredRole) => rolesToCheck.includes(requiredRole));
  });

  if (isKioskModeActive.value) {
    return roleFiltered.filter((item) => KIOSK_ALLOWED_ROUTE_NAMES.includes(item.routeName as "visitors" | "delays"));
  }

  return roleFiltered;
});
</script>

<style scoped>
.icon-container {
  min-width: 4rem;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.nav-item {
  display: flex;
  align-items: center;
  height: 3.5rem;
  color: var(--vtmn-color_grey-dark-1);
  transition: background-color 0.2s;
  overflow: hidden;
  white-space: nowrap;
  cursor: pointer;
}

.nav-item:hover {
  background-color: var(--vtmn-color_blue-light-5);
}

.router-link-exact-active .icon-container {
  margin-left: -4px;
}

.nav-text {
  transition: opacity 0.3s ease-in-out;
  transition-delay: 0.1s;
}

.nav-item-active,
.router-link-active.nav-item {
  background-color: var(--vtmn-color_blue-light-4);
  color: var(--vtmn-color_blue-dark-2);
  border-left: 4px solid var(--vtmn-color_blue-dark-1);
}

.nav-item-active .icon-container,
.router-link-active .icon-container {
  margin-left: -4px;
}
</style>
